import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo } from 'react';
import { Modal } from 'react-bootstrap';
import AssetService from '../../services/AssetService';
import FacilityService from '../../services/FacilityService';
import AssetInventoryService from '../../services/AssetInventoryService';
import { If } from "../../utils/Controls";
import { AntdPaging, AntdSelect, AntdDatepicker } from "../../utils/Antd";
import util from "../../utils/util";
import AssetsReportUtil from '../../utils/AssetsReportUtil';
import ExcelRportUtil from '../../utils/excelUtils/ExcelAssetInventoryReportUtil';
import search from '../assets/icons/magnifying-glass-solid.svg'
import AssetInventorySummary from '../../utils/AssetInventorySummary'
import clipboard from "../assets/icons/clipboards.png";
import excel from "../assets/icons/excel.png";
import acrobat from "../assets/icons/acrobat.png";
import Workplanintegration from '../Workplanintegration';
import { CalendarOutlined } from '@ant-design/icons';
import {
    Button,
    message,
    Tabs,
    Card,
    Row,
    Col,
    Statistic,
    Progress,
    Badge,
    Tooltip,
    Table,
    Divider,
    
} from 'antd';
import '../assets/css/styles.css';
// Add this new component before your InventoryAnalytics component
const InventoryDataIntelligence = ({ data, clients, assetType, asset_cat_id, isVisible }) => {
    const [intelligence, setIntelligence] = useState({
        facilitiesWithNoData: [],
        incompleteAssetRecords: [],
        missingValueData: [],
        statusInconsistencies: [],
        zeroQuantityAssets: [],
        criticalDataGaps: [],
        recommendations: [],
        completenessScore: 0,
        dataHealthMetrics: {}
    });

    useEffect(() => {
        if (data && data.length > 0 && clients && clients.length > 0) {
            analyzeInventoryDataCompleteness();
        }
    }, [data, clients, asset_cat_id]);

 const analyzeInventoryDataCompleteness = () => {
    // Get all facility IDs from clients
    const allFacilityIds = clients.map(c => c.id);
    
    // Identify facilities with no inventory data OR zero totalitems
    const facilitiesWithData = [...new Set(data.map(item => item.client_id))];
    const facilitiesWithZeroData = [];

    // Check for facilities with no data at all
    const facilitiesWithNoData = clients.filter(client => 
        !facilitiesWithData.includes(client.id)
    ).map(facility => ({
        ...facility,
        issue: 'No inventory data captured',
        severity: 'high',
        impact: 'Cannot track assets, complete audit, or make informed decisions',
        totalItems: 0
    }));

    // Check for facilities with data but zero totalitems
    clients.forEach(client => {
        const clientData = data.filter(item => item.client_id === client.id);
        if (clientData.length > 0) {
            const totalItems = clientData.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
            if (totalItems === 0) {
                facilitiesWithZeroData.push({
                    ...client,
                    issue: 'All inventory records show zero quantities',
                    severity: 'high',
                    impact: 'Has inventory records but no actual assets counted',
                    totalItems: 0,
                    recordCount: clientData.length
                });
            }
        }
    });

    // Combine both types of zero-data facilities
    const allFacilitiesWithZeroData = [...facilitiesWithNoData, ...facilitiesWithZeroData];

    // Analyze data completeness for existing records
    let incompleteAssetRecords = [];
    let missingValueData = [];
    let statusInconsistencies = [];
    let zeroQuantityAssets = [];
    let totalCompleteness = 0;

    data.forEach(record => {
        let recordIssues = [];
        let recordScore = 0;
        const maxScore = 10; // Total possible score

        // Check for essential fields
        if (record.item && record.item.trim()) recordScore += 2;
        else recordIssues.push('Missing item name');

        if (record.totalitems && parseInt(record.totalitems) > 0) recordScore += 2;
        else recordIssues.push('Zero or missing total quantity');

        if (record.totalvalue && parseFloat(record.totalvalue) > 0) recordScore += 2;
        else recordIssues.push('Missing or zero total value');

        // Check status-specific fields based on asset category
        if (asset_cat_id === '8') { // Vehicles
            if (record.good !== undefined && record.fair !== undefined && 
                record.repairs !== undefined && record.disposal !== undefined) {
                recordScore += 2;
            } else {
                recordIssues.push('Missing vehicle condition data');
            }
        } else if (asset_cat_id === '9') { // Supplies
            if (record.usable !== undefined && record.expired !== undefined) {
                recordScore += 2;
            } else {
                recordIssues.push('Missing supply status data');
            }
        } else { // Medical/Non-Medical
            if (record.functional !== undefined && record.partialfunctional !== undefined && 
                record.nonfunctional !== undefined) {
                recordScore += 2;
            } else {
                recordIssues.push('Missing functional status data');
            }
        }

        // Check client information
        if (record.client && record.client.trim()) recordScore += 1;
        else recordIssues.push('Missing facility information');

        // Check for station point data
        if (record.station_point) recordScore += 1;
        else recordIssues.push('Missing location/station point');

        const completionPercentage = (recordScore / maxScore) * 100;
        totalCompleteness += completionPercentage;

        // Categorize issues
        if (recordIssues.length > 0) {
            incompleteAssetRecords.push({
                ...record,
                issues: recordIssues,
                completionPercentage,
                severity: completionPercentage < 50 ? 'high' : completionPercentage < 80 ? 'medium' : 'low'
            });
        }

        // Check for missing value data specifically
        if (!record.totalvalue || parseFloat(record.totalvalue) === 0) {
            missingValueData.push({
                ...record,
                issue: 'Missing asset valuation',
                impact: 'Cannot calculate total asset value or depreciation'
            });
        }

        // Check for zero quantities
        if (!record.totalitems || parseInt(record.totalitems) === 0) {
            zeroQuantityAssets.push({
                ...record,
                issue: 'Zero quantity recorded',
                impact: 'May indicate data entry error or obsolete record'
            });
        }

        // Check for status inconsistencies
        const checkStatusConsistency = (record) => {
            const total = parseInt(record.totalitems) || 0;
            let statusSum = 0;

            if (asset_cat_id === '8') {
                statusSum = (parseInt(record.good) || 0) + (parseInt(record.fair) || 0) + 
                           (parseInt(record.repairs) || 0) + (parseInt(record.disposal) || 0);
            } else if (asset_cat_id === '9') {
                statusSum = (parseInt(record.usable) || 0) + (parseInt(record.expired) || 0);
            } else {
                statusSum = (parseInt(record.functional) || 0) + (parseInt(record.partialfunctional) || 0) + 
                           (parseInt(record.nonfunctional) || 0) + (parseInt(record.installed) || 0) + 
                           (parseInt(record.not_installed) || 0) + (parseInt(record.boarded) || 0);
            }

            if (total > 0 && Math.abs(total - statusSum) > 0) {
                return {
                    ...record,
                    issue: `Status breakdown doesn't match total (${statusSum} vs ${total})`,
                    severity: 'medium',
                    impact: 'Affects accuracy of status reporting'
                };
            }
            return null;
        };

        const statusIssue = checkStatusConsistency(record);
        if (statusIssue) {
            statusInconsistencies.push(statusIssue);
        }
    });

    // Generate intelligent recommendations
    let recommendations = [];
    let criticalDataGaps = [];

    if (allFacilitiesWithZeroData.length > 0) {
        const facilitiesByRegion = allFacilitiesWithZeroData.reduce((acc, f) => {
            const region = f.located_region || 'Unknown';
            acc[region] = (acc[region] || 0) + 1;
            return acc;
        }, {});

        recommendations.push({
            type: 'no_data',
            priority: 'critical',
            message: `${allFacilitiesWithZeroData.length} facilities have zero ${assetType.toLowerCase()} inventory`,
            action: 'Initiate data collection and verification for these facilities immediately',
            facilities: allFacilitiesWithZeroData.slice(0, 5).map(f => f.name),
            details: `Most affected regions: ${Object.entries(facilitiesByRegion)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([region, count]) => `${region} (${count})`)
                .join(', ')}`,
            totalCount: allFacilitiesWithZeroData.length
        });

        criticalDataGaps.push({
            type: 'missing_facilities',
            count: allFacilitiesWithZeroData.length,
            impact: 'Unable to track assets across entire healthcare system'
        });
    }

    if (missingValueData.length > 0) {
        recommendations.push({
            type: 'missing_values',
            priority: 'high',
            message: `${missingValueData.length} asset records missing valuation data`,
            action: 'Update asset values for proper financial tracking',
            impact: 'Cannot calculate total asset portfolio value'
        });
    }

    if (zeroQuantityAssets.length > 0) {
        recommendations.push({
            type: 'zero_quantities',
            priority: 'medium',
            message: `${zeroQuantityAssets.length} records show zero quantities`,
            action: 'Review and update or archive obsolete records',
            impact: 'May indicate data entry errors or outdated records'
        });
    }

    if (incompleteAssetRecords.length > 0) {
        const highSeverity = incompleteAssetRecords.filter(r => r.severity === 'high').length;
        if (highSeverity > 0) {
            recommendations.push({
                type: 'incomplete_records',
                priority: 'high',
                message: `${highSeverity} asset records are critically incomplete (<50% complete)`,
                action: 'Complete missing data fields for accurate reporting',
                impact: 'Reduces reliability of inventory analysis and decision-making'
            });
        }
    }

    if (statusInconsistencies.length > 0) {
        recommendations.push({
            type: 'status_inconsistencies',
            priority: 'medium',
            message: `${statusInconsistencies.length} records have status breakdown mismatches`,
            action: 'Verify and correct status quantities',
            impact: 'Affects accuracy of functional status reporting'
        });
    }

    const overallCompleteness = data.length > 0 ? totalCompleteness / data.length : 0;

    // Calculate data health metrics
    const dataHealthMetrics = {
        facilityCoverage: ((clients.length - facilitiesWithNoData.length) / clients.length) * 100,
        valueCompleteness: ((data.length - missingValueData.length) / data.length) * 100,
        quantityAccuracy: ((data.length - zeroQuantityAssets.length) / data.length) * 100,
        statusConsistency: ((data.length - statusInconsistencies.length) / data.length) * 100
    };

    setIntelligence({
        facilitiesWithNoData: allFacilitiesWithZeroData,
        incompleteAssetRecords: incompleteAssetRecords.slice(0, 20),
        missingValueData: missingValueData.slice(0, 10),
        statusInconsistencies: statusInconsistencies.slice(0, 10),
        zeroQuantityAssets: zeroQuantityAssets.slice(0, 10),
        criticalDataGaps,
        recommendations: recommendations.sort((a, b) => 
            (a.priority === 'critical' ? 0 : a.priority === 'high' ? 1 : 2) - 
            (b.priority === 'critical' ? 0 : b.priority === 'high' ? 1 : 2)
        ),
        completenessScore: overallCompleteness,
        dataHealthMetrics
    });
};
    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'critical': return '#ff4d4f';
            case 'high': return '#fa541c';
            case 'medium': return '#faad14';
            default: return '#1890ff';
        }
    };

    const getHealthColor = (percentage) => {
        if (percentage >= 90) return '#52c41a';
        if (percentage >= 75) return '#faad14';
        if (percentage >= 50) return '#fa8c16';
        return '#ff4d4f';
    };

    const getHealthLabel = (percentage) => {
        if (percentage >= 90) return 'Excellent';
        if (percentage >= 75) return 'Good';
        if (percentage >= 50) return 'Fair';
        return 'Poor';
    };

    if (!isVisible) return null;

    return (
        <div className="inventory-intelligence-panel" style={{
            marginBottom: '20px',
            padding: '20px',
            background: '#fff7e6',
            borderRadius: '8px',
            border: '1px solid #ffd591'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#d48806' }}>
                    🎯 Inventory Data Intelligence - {assetType}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{ fontSize: '14px', color: '#8c8c8c' }}>Data Completeness:</span>
                    <div style={{
                        padding: '4px 12px',
                        borderRadius: '16px',
                        backgroundColor: getHealthColor(intelligence.completenessScore),
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '14px'
                    }}>
                        {intelligence.completenessScore.toFixed(1)}%
                    </div>
                </div>
            </div>

            {/* Data Health Overview */}
            <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Facility Coverage"
                            value={intelligence.dataHealthMetrics.facilityCoverage}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: getHealthColor(intelligence.dataHealthMetrics.facilityCoverage) }}
                        />
                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                            {clients.length - intelligence.facilitiesWithNoData.length}/{clients.length} facilities
                        </div>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Value Data Complete"
                            value={intelligence.dataHealthMetrics.valueCompleteness}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: getHealthColor(intelligence.dataHealthMetrics.valueCompleteness) }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Status Consistency"
                            value={intelligence.dataHealthMetrics.statusConsistency}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: getHealthColor(intelligence.dataHealthMetrics.statusConsistency) }}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Data Health Score"
                            value={intelligence.completenessScore}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: getHealthColor(intelligence.completenessScore) }}
                        />
                        <Badge 
                            color={getHealthColor(intelligence.completenessScore)} 
                            text={getHealthLabel(intelligence.completenessScore)} 
                        />
                    </Card>
                </Col>
            </Row>


            {/* Detailed Data Issues */}
            <Row gutter={[16, 16]}>
                {intelligence.facilitiesWithNoData.length > 0 && (
    <Col xs={24} md={12}>
        <Card 
            size="small" 
            title={`Facilities with Zero ${assetType} Data`}
            extra={
                <Badge 
                    count={intelligence.facilitiesWithNoData.length} 
                    style={{ backgroundColor: '#ff4d4f' }}
                />
            }
        >
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {intelligence.facilitiesWithNoData.map((facility, index) => (
                    <div key={index} style={{
                        padding: '8px',
                        marginBottom: '4px',
                        background: '#fff2f0',
                        borderRadius: '4px',
                        borderLeft: '3px solid #ff4d4f'
                    }}>
                        <div style={{ fontWeight: '500', fontSize: '12px' }}>
                            {facility.name}
                        </div>
                        <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                            {facility.located_region || 'Unknown Region'} | {facility.site_type || 'Unknown Level'}
                        </div>
                        <div style={{ fontSize: '10px', color: '#ff4d4f', marginTop: '2px' }}>
                            {facility.issue}
                            {facility.recordCount && (
                                <span style={{ marginLeft: '8px', color: '#fa8c16' }}>
                                    ({facility.recordCount} records with zero items)
                                </span>
                            )}
                        </div>
                    </div>
                ))}
                {intelligence.facilitiesWithNoData.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#52c41a' }}>
                        All facilities have inventory data recorded!
                    </div>
                )}
            </div>
        </Card>
    </Col>
)}

                {intelligence.incompleteAssetRecords.length > 0 && (
                    <Col xs={24} md={12}>
                        <Card 
                            size="small" 
                            title="Incomplete Asset Records"
                            extra={
                                <Badge 
                                    count={intelligence.incompleteAssetRecords.length} 
                                    style={{ backgroundColor: '#fa8c16' }}
                                />
                            }
                        >
                            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {intelligence.incompleteAssetRecords.slice(0, 8).map((record, index) => (
                                    <div key={index} style={{
                                        padding: '8px',
                                        marginBottom: '4px',
                                        background: record.severity === 'high' ? '#fff2f0' : '#fff7e6',
                                        borderRadius: '4px',
                                        borderLeft: `3px solid ${record.severity === 'high' ? '#ff4d4f' : '#faad14'}`
                                    }}>
                                        <div style={{ fontWeight: '500', fontSize: '12px' }}>
                                            {record.item} - {record.client}
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: '2px' }}>
                                            Completeness: {record.completionPercentage.toFixed(1)}%
                                        </div>
                                        <div style={{ fontSize: '10px', color: '#666' }}>
                                            Issues: {record.issues.join(', ')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    </Col>
                )}
            </Row>

            {/* Status Inconsistencies and Value Issues */}
            {(intelligence.statusInconsistencies.length > 0 || intelligence.missingValueData.length > 0) && (
                <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                    {intelligence.statusInconsistencies.length > 0 && (
                        <Col xs={24} md={12}>
                            <Card 
                                size="small" 
                                title="Status Inconsistencies"
                                extra={
                                    <Badge 
                                        count={intelligence.statusInconsistencies.length} 
                                        style={{ backgroundColor: '#faad14' }}
                                    />
                                }
                            >
                                <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                    {intelligence.statusInconsistencies.slice(0, 5).map((record, index) => (
                                        <div key={index} style={{
                                            padding: '6px',
                                            marginBottom: '4px',
                                            background: '#fffbe6',
                                            borderRadius: '4px'
                                        }}>
                                            <div style={{ fontWeight: '500', fontSize: '11px' }}>
                                                {record.item}
                                            </div>
                                            <div style={{ fontSize: '10px', color: '#fa8c16' }}>
                                                {record.issue}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        </Col>
                    )}

                </Row>
            )}
        </div>
    );
};

// Enhanced List component with intelligence toggle
// Add this to your List component's state variables:
//const [showIntelligence, setShowIntelligence] = useState(true);

// Add this toggle to your page header (in InventoryReport component):

// Comparative Analytics Component
const InventoryAnalytics = ({ data, clients, isNational, assetType, asset_cat_id, selectedLevel, selectedRegion }) => {
    const [analytics, setAnalytics] = useState({
        totalAssets: 0,
        totalValue: 0,
        functionalRate: 0,
        regionalComparison: [],
        levelComparison: [],
        facilityRankings: [],
        facilityStockLevels: [],
        performanceMetrics: {},
        distributionStats: {}
    });

    const [showTopFacilities, setShowTopFacilities] = useState(true); // Toggle for top/bottom 20
    const analyticsRef = useRef(); // Reference for export functionality

    // Export functions
    const exportToPDF = async () => {
        try {
            util.showLoader();
            
            // Use html2canvas to capture the analytics section
            const html2canvas = await import('html2canvas');
            const jsPDF = await import('jspdf');
            
            if (analyticsRef.current) {
                const canvas = await html2canvas.default(analyticsRef.current, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false
                });
                
                const imgData = canvas.toDataURL('image/png');
                const pdf = new jsPDF.jsPDF('landscape', 'mm', 'a4');
                
                const imgWidth = 297; // A4 landscape width in mm
                const pageHeight = 210; // A4 landscape height in mm
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                let heightLeft = imgHeight;
                
                let position = 0;
                
                // Add title
                pdf.setFontSize(16);
                pdf.text(`Asset Inventory Analytics - ${assetType}`, 20, 20);
                pdf.setFontSize(12);
                pdf.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
                
                position = 40;
                
                // Add image
                pdf.addImage(imgData, 'PNG', 10, position, imgWidth - 20, imgHeight);
                heightLeft -= pageHeight;
                
                // Add new pages if needed
                while (heightLeft >= 0) {
                    position = heightLeft - imgHeight + 40;
                    pdf.addPage();
                    pdf.addImage(imgData, 'PNG', 10, position, imgWidth - 20, imgHeight);
                    heightLeft -= pageHeight;
                }
                
                pdf.save(`inventory-analytics-${assetType.toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
                message.success('Analytics exported to PDF successfully!');
            }
        } catch (error) {
            console.error('Error exporting to PDF:', error);
            message.error('Failed to export to PDF. Please try again.');
        } finally {
            util.hideLoader();
        }
    };

    const exportToJPG = async () => {
        try {
            util.showLoader();
            
            // Use html2canvas to capture the analytics section
            const html2canvas = await import('html2canvas');
            
            if (analyticsRef.current) {
                const canvas = await html2canvas.default(analyticsRef.current, {
                    scale: 3,
                    useCORS: true,
                    backgroundColor: '#ffffff',
                    logging: false
                });
                
                // Convert to JPG and download
                canvas.toBlob((blob) => {
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `inventory-analytics-${assetType.toLowerCase()}-${new Date().toISOString().split('T')[0]}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    message.success('Analytics exported to JPG successfully!');
                }, 'image/jpeg', 0.95);
            }
        } catch (error) {
            console.error('Error exporting to JPG:', error);
            message.error('Failed to export to JPG. Please try again.');
        } finally {
            util.hideLoader();
        }
    };

    useEffect(() => {
        if (data && data.length > 0 && clients && clients.length > 0) {
            console.log('Analytics Data:', data);
            console.log('Clients Data:', clients);
            calculateAnalytics(data, clients);
        }
    }, [data, clients, selectedLevel, selectedRegion]);

    // Calculate facility analytics with filters applied
    const calculateAnalytics = (inventoryData, facilitiesData, summaryData = []) => {
        // Calculate basic metrics using totalitems from API data
        const totalAssets = inventoryData.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
        const totalValue = inventoryData.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0);
        
        // Get functional count based on asset category
        const getFunctionalCount = (item) => {
            switch (asset_cat_id) {
                case '3':
                case '2':
                    return parseInt(item.functional) || 0;
                case '8':
                    return parseInt(item.good) || 0;
                case '9':
                    return parseInt(item.usable) || 0;
                default:
                    return parseInt(item.functional) || 0;
            }
        };

        const totalFunctional = inventoryData.reduce((sum, item) => sum + getFunctionalCount(item), 0);
        const functionalRate = totalAssets > 0 ? (totalFunctional / totalAssets) * 100 : 0;

        // Apply search filters to facilities data for analytics
        let filteredFacilities = facilitiesData;
        
        // Filter by region if selected
        if (selectedRegion) {
            filteredFacilities = filteredFacilities.filter(f => f.region === selectedRegion);
        }
        
        // Filter by level if selected  
        if (selectedLevel) {
            filteredFacilities = filteredFacilities.filter(f => f.site_type === selectedLevel);
        }

        // Create detailed facility stock levels with rates
        const facilityStockLevels = filteredFacilities.map(facility => {
            const facilityAssets = inventoryData.filter(item => {
                // Match by facility ID or name
                return item.client_id === facility.id || 
                       item.client === facility.business_name || 
                       item.client === facility.name;
            });
            
            // Calculate totals per facility
            const totalAssets = facilityAssets.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
            const functionalAssets = facilityAssets.reduce((sum, item) => sum + getFunctionalCount(item), 0);
            
            // Calculate other status counts based on asset category
            let statusBreakdown = {};
            if (asset_cat_id === '8') { // Vehicles
                statusBreakdown = {
                    good: facilityAssets.reduce((sum, item) => sum + (parseInt(item.good) || 0), 0),
                    fair: facilityAssets.reduce((sum, item) => sum + (parseInt(item.fair) || 0), 0),
                    repairs: facilityAssets.reduce((sum, item) => sum + (parseInt(item.repairs) || 0), 0),
                    disposal: facilityAssets.reduce((sum, item) => sum + (parseInt(item.disposal) || 0), 0)
                };
            } else if (asset_cat_id === '9') { // Supplies
                statusBreakdown = {
                    usable: facilityAssets.reduce((sum, item) => sum + (parseInt(item.usable) || 0), 0),
                    expired: facilityAssets.reduce((sum, item) => sum + (parseInt(item.expired) || 0), 0)
                };
            } else { // Medical/Non-Medical
                statusBreakdown = {
                    functional: facilityAssets.reduce((sum, item) => sum + (parseInt(item.functional) || 0), 0),
                    partialfunctional: facilityAssets.reduce((sum, item) => sum + (parseInt(item.partialfunctional) || 0), 0),
                    nonfunctional: facilityAssets.reduce((sum, item) => sum + (parseInt(item.nonfunctional) || 0), 0),
                    installed: facilityAssets.reduce((sum, item) => sum + (parseInt(item.installed) || 0), 0),
                    not_installed: facilityAssets.reduce((sum, item) => sum + (parseInt(item.not_installed) || 0), 0),
                    boarded: facilityAssets.reduce((sum, item) => sum + (parseInt(item.boarded) || 0), 0)
                };
            }

            const functionalRate = totalAssets > 0 ? (functionalAssets / totalAssets) * 100 : 0;
            const totalValue = facilityAssets.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0);

            return {
                ...facility,
                totalAssets,
                functionalAssets,
                functionalRate,
                totalValue,
                statusBreakdown,
                // Ensure we have the facility name for display
                facilityName: facility.business_name || facility.name || 'Unknown Facility'
            };
        }).filter(f => f.totalAssets > 0);

        // Create facility performance map using totalitems from API with filters applied
        const facilityPerformance = filteredFacilities.map(facility => {
            const facilityAssets = inventoryData.filter(item => {
                return item.client_id === facility.id || 
                       item.client === facility.business_name || 
                       item.client === facility.name;
            });
            const facilityTotal = facilityAssets.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
            const facilityFunctional = facilityAssets.reduce((sum, item) => sum + getFunctionalCount(item), 0);
            const facilityRate = facilityTotal > 0 ? (facilityFunctional / facilityTotal) * 100 : 0;

            return {
                ...facility,
                totalAssets: facilityTotal,
                functionalAssets: facilityFunctional,
                functionalRate: facilityRate,
                totalValue: facilityAssets.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0),
                facilityName: facility.business_name || facility.name || 'Unknown Facility'
            };
        }).filter(f => f.totalAssets > 0);

        // Regional comparison using totalitems from API with filters applied
        const regionalStats = facilitiesData.reduce((acc, facility) => {
            // Only process facilities that have region data
            if (!facility.region) return acc;
            
            const facilityAssets = inventoryData.filter(item => {
                return item.client_id === facility.id || 
                       item.client === facility.business_name || 
                       item.client === facility.name;
            });
            const facilityTotal = facilityAssets.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
            const facilityFunctional = facilityAssets.reduce((sum, item) => sum + getFunctionalCount(item), 0);
            const facilityRate = facilityTotal > 0 ? (facilityFunctional / facilityTotal) * 100 : 0;

            // Initialize region if not exists
            if (!acc[facility.region]) {
                acc[facility.region] = {
                    region: facility.region,
                    totalAssets: 0,
                    functionalAssets: 0,
                    facilitiesCount: 0,
                    totalValue: 0,
                    topFacility: null,
                    topFacilityRate: 0
                };
            }

            // Add facility data to region totals
            acc[facility.region].totalAssets += facilityTotal;
            acc[facility.region].functionalAssets += facilityFunctional;
            acc[facility.region].facilitiesCount += 1;
            acc[facility.region].totalValue += facilityAssets.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0);

            // Track top performing facility in this region
            if (facilityTotal > 0 && facilityRate > acc[facility.region].topFacilityRate) {
                acc[facility.region].topFacility = facility.business_name || facility.name || 'Unknown';
                acc[facility.region].topFacilityRate = facilityRate;
            }

            return acc;
        }, {});

        // Ensure we have data for all expected regions
        const expectedRegions = ['Manzini', 'Hhohho', 'Shiselweni', 'Lubombo'];
        expectedRegions.forEach(region => {
            if (!regionalStats[region]) {
                regionalStats[region] = {
                    region: region,
                    totalAssets: 0,
                    functionalAssets: 0,
                    facilitiesCount: 0,
                    totalValue: 0,
                    topFacility: 'No facilities',
                    topFacilityRate: 0
                };
            }
        });

        const regionalComparison = Object.values(regionalStats).map(region => ({
            ...region,
            functionalRate: region.totalAssets > 0 ? (region.functionalAssets / region.totalAssets) * 100 : 0
        })).sort((a, b) => b.functionalRate - a.functionalRate);

        // Level comparison using totalitems from API with filters applied
        const levelStats = facilitiesData.reduce((acc, facility) => {
            // Only process facilities that have level data
            if (!facility.site_type) return acc;
            
            const facilityAssets = inventoryData.filter(item => {
                return item.client_id === facility.id || 
                       item.client === facility.business_name || 
                       item.client === facility.name;
            });
            const facilityTotal = facilityAssets.reduce((sum, item) => sum + (parseInt(item.totalitems) || 0), 0);
            const facilityFunctional = facilityAssets.reduce((sum, item) => sum + getFunctionalCount(item), 0);
            const facilityRate = facilityTotal > 0 ? (facilityFunctional / facilityTotal) * 100 : 0;

            // Initialize level if not exists
            if (!acc[facility.site_type]) {
                acc[facility.site_type] = {
                    level: facility.site_type,
                    totalAssets: 0,
                    functionalAssets: 0,
                    facilitiesCount: 0,
                    totalValue: 0,
                    topFacility: null,
                    topFacilityRate: 0
                };
            }

            // Add facility data to level totals
            acc[facility.site_type].totalAssets += facilityTotal;
            acc[facility.site_type].functionalAssets += facilityFunctional;
            acc[facility.site_type].facilitiesCount += 1;
            acc[facility.site_type].totalValue += facilityAssets.reduce((sum, item) => sum + (parseFloat(item.totalvalue) || 0), 0);

            // Track top performing facility in this level
            if (facilityTotal > 0 && facilityRate > acc[facility.site_type].topFacilityRate) {
                acc[facility.site_type].topFacility = facility.business_name || facility.name || 'Unknown';
                acc[facility.site_type].topFacilityRate = facilityRate;
            }

            return acc;
        }, {});

        // Ensure we have data for all expected levels
        const expectedLevels = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5'];
        expectedLevels.forEach(level => {
            if (!levelStats[level]) {
                levelStats[level] = {
                    level: level,
                    totalAssets: 0,
                    functionalAssets: 0,
                    facilitiesCount: 0,
                    totalValue: 0,
                    topFacility: 'No facilities',
                    topFacilityRate: 0
                };
            }
        });

        const levelComparison = Object.values(levelStats).map(level => ({
            ...level,
            functionalRate: level.totalAssets > 0 ? (level.functionalAssets / level.totalAssets) * 100 : 0
        })).sort((a, b) => b.functionalRate - a.functionalRate);

        console.log('Facilities Data Sample:', facilitiesData.slice(0, 3));
        console.log('Inventory Data Sample:', inventoryData.slice(0, 3));
        console.log('Regional Comparison Data:', regionalComparison);
        console.log('Level Comparison Data:', levelComparison);

        // Facility rankings (top 10) from filtered data
        const facilityRankings = facilityPerformance
            .sort((a, b) => b.functionalRate - a.functionalRate)
            .slice(0, 10);

        setAnalytics({
            totalAssets,
            totalValue,
            functionalRate,
            regionalComparison,
            levelComparison,
            facilityRankings,
            facilityStockLevels,
            performanceMetrics: {
                averageRegionalRate: regionalComparison.reduce((sum, r) => sum + r.functionalRate, 0) / regionalComparison.length || 0,
                averageLevelRate: levelComparison.reduce((sum, l) => sum + l.functionalRate, 0) / levelComparison.length || 0,
                topPerformingRegion: regionalComparison[0]?.region || 'N/A',
                topPerformingLevel: levelComparison[0]?.level || 'N/A'
            },
            distributionStats: {
                facilitiesWithAssets: facilityPerformance.length,
                totalFacilities: facilitiesData.length
            }
        });
    };

    const getFunctionalLabel = () => {
        switch (asset_cat_id) {
            case '3':
            case '2':
                return 'Serviceable Rate';
            case '8':
                return 'Good Condition Rate';
            case '9':
                return 'Usable Rate';
            default:
                return 'Functional Rate';
        }
    };

    const getPerformanceColor = (rate) => {
        if (rate >= 80) return '#52c41a';
        if (rate >= 60) return '#faad14';
        if (rate >= 40) return '#ff7a45';
        return '#ff4d4f';
    };

    const getPerformanceText = (rate) => {
        if (rate >= 80) return 'Excellent';
        if (rate >= 60) return 'Good';
        if (rate >= 40) return 'Fair';
        return 'Poor';
    };

    // Get top 20 or bottom 20 facilities based on toggle
    const getTopBottomFacilities = () => {
        const sortedFacilities = [...analytics.facilityStockLevels].sort((a, b) => 
            showTopFacilities ? b.functionalRate - a.functionalRate : a.functionalRate - b.functionalRate
        );
        return sortedFacilities.slice(0, 20);
    };

    const getStatusBreakdownDisplay = (facility) => {
        const breakdown = facility.statusBreakdown;
        switch (asset_cat_id) {
            case '8': // Vehicles
                return (
                    <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
                        <div>Good: {breakdown.good} | Fair: {breakdown.fair}</div>
                        <div>Repairs: {breakdown.repairs} | Disposal: {breakdown.disposal}</div>
                    </div>
                );
            case '9': // Supplies
                return (
                    <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
                        <div>Usable: {breakdown.usable} | Expired: {breakdown.expired}</div>
                    </div>
                );
            default: // Medical/Non-Medical
                return (
                    <div style={{ fontSize: '11px', lineHeight: '1.2' }}>
                        <div>Functional: {breakdown.functional}</div>
                        <div>Non-func: {breakdown.nonfunctional}</div>
                        <div>Boarded: {breakdown.boarded}</div>
                    </div>
                );
        }
    };

    // Filter comparisons based on selected level/region
    const filteredLevelComparison = selectedLevel ? 
        analytics.levelComparison.filter(l => l.level === selectedLevel) : 
        analytics.levelComparison;

    const filteredRegionalComparison = selectedRegion ? 
        analytics.regionalComparison.filter(r => r.region === selectedRegion) : 
        analytics.regionalComparison;

    const facilityStockColumns = [
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            width: 60,
            align: 'center',
            render: (text, record, index) => (
                <Badge 
                    color={index < 5 ? '#52c41a' : index < 10 ? '#faad14' : '#ff4d4f'} 
                    text={`#${index + 1}`}
                />
            )
        },
        {
            title: 'Facility',
            dataIndex: 'facilityName',
            key: 'facility',
            width: 200,
            render: (text, record) => (
                <div>
                    <Tooltip title={text || record.business_name || record.name}>
                        <div style={{ fontWeight: 'bold', fontSize: '12px' }}>
                            {(text || record.business_name || record.name || 'Unknown Facility').length > 25 ? 
                                `${(text || record.business_name || record.name || 'Unknown Facility').substring(0, 25)}...` : 
                                (text || record.business_name || record.name || 'Unknown Facility')}
                        </div>
                    </Tooltip>
                    <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                        {record.region || 'N/A'} | {record.site_type || 'N/A'}
                    </div>
                </div>
            )
        },
        {
            title: 'Total Assets',
            dataIndex: 'totalAssets',
            key: 'totalAssets',
            width: 100,
            align: 'center',
            render: (value) => (
                <span style={{ fontWeight: 'bold', color: '#1890ff' }}>
                    {value}
                </span>
            )
        },
        {
            title: getFunctionalLabel(),
            dataIndex: 'functionalRate',
            key: 'functionalRate',
            width: 120,
            align: 'center',
            render: (rate, record) => (
                <div>
                    <div style={{ 
                        fontSize: '14px', 
                        fontWeight: 'bold', 
                        color: getPerformanceColor(rate) 
                    }}>
                        {rate.toFixed(1)}%
                    </div>
                    <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                        {record.functionalAssets}/{record.totalAssets}
                    </div>
                </div>
            )
        },
        {
            title: 'Status Breakdown',
            dataIndex: 'statusBreakdown',
            key: 'statusBreakdown',
            width: 180,
            render: (breakdown, record) => getStatusBreakdownDisplay(record)
        },
        {
            title: 'Total Value',
            dataIndex: 'totalValue',
            key: 'totalValue',
            width: 100,
            align: 'center',
            render: (value) => (
                <span style={{ fontWeight: 'bold', color: '#722ed1' }}>
                    E{value.toFixed(0)}
                </span>
            )
        },
        {
            title: 'Performance',
            dataIndex: 'functionalRate',
            key: 'performance',
            width: 100,
            align: 'center',
            render: (rate) => (
                <Badge 
                    color={getPerformanceColor(rate)} 
                    text={getPerformanceText(rate)}
                />
            )
        }
    ];

    const regionalColumns = [
        {
            title: 'Region',
            dataIndex: 'region',
            key: 'region',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Facilities',
            dataIndex: 'facilitiesCount',
            key: 'facilitiesCount',
            align: 'center'
        },
        {
            title: 'Total Assets',
            dataIndex: 'totalAssets',
            key: 'totalAssets',
            align: 'center'
        },
        {
            title: getFunctionalLabel(),
            dataIndex: 'functionalRate',
            key: 'functionalRate',
            align: 'center',
            render: (rate) => (
                <Badge 
                    color={getPerformanceColor(rate)} 
                    text={`${rate.toFixed(1)}%`}
                />
            )
        },
        {
            title: 'Top Performing Facility',
            dataIndex: 'topFacility',
            key: 'topFacility',
            width: 200,
            render: (text, record) => (
                <div>
                    <Tooltip title={text}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {text && text.length > 20 ? `${text.substring(0, 20)}...` : text || 'N/A'}
                        </div>
                    </Tooltip>
                    <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                        {record.topFacilityRate > 0 ? `${record.topFacilityRate.toFixed(1)}% rate` : 'No data'}
                    </div>
                </div>
            )
        },
        {
            title: 'Performance',
            dataIndex: 'functionalRate',
            key: 'performance',
            align: 'center',
            render: (rate) => (
                <span style={{ color: getPerformanceColor(rate), fontWeight: 'bold' }}>
                    {getPerformanceText(rate)}
                </span>
            )
        }
    ];

    const levelColumns = [
        {
            title: 'Level',
            dataIndex: 'level',
            key: 'level',
            render: (text) => <strong>{text}</strong>
        },
        {
            title: 'Facilities',
            dataIndex: 'facilitiesCount',
            key: 'facilitiesCount',
            align: 'center'
        },
        {
            title: 'Total Assets',
            dataIndex: 'totalAssets',
            key: 'totalAssets',
            align: 'center'
        },
        {
            title: getFunctionalLabel(),
            dataIndex: 'functionalRate',
            key: 'functionalRate',
            align: 'center',
            render: (rate) => (
                <Badge 
                    color={getPerformanceColor(rate)} 
                    text={`${rate.toFixed(1)}%`}
                />
            )
        },
        {
            title: 'Top Performing Facility',
            dataIndex: 'topFacility',
            key: 'topFacility',
            width: 200,
            render: (text, record) => (
                <div>
                    <Tooltip title={text}>
                        <div style={{ fontSize: '12px', fontWeight: 'bold' }}>
                            {text && text.length > 20 ? `${text.substring(0, 20)}...` : text || 'N/A'}
                        </div>
                    </Tooltip>
                    <div style={{ fontSize: '10px', color: '#8c8c8c' }}>
                        {record.topFacilityRate > 0 ? `${record.topFacilityRate.toFixed(1)}% rate` : 'No data'}
                    </div>
                </div>
            )
        },
        {
            title: 'Performance',
            dataIndex: 'functionalRate',
            key: 'performance',
            align: 'center',
            render: (rate) => (
                <span style={{ color: getPerformanceColor(rate), fontWeight: 'bold' }}>
                    {getPerformanceText(rate)}
                </span>
            )
        }
    ];

    return (
        <div className="analytics-section mb-4" ref={analyticsRef}>
            {/* Analytics Header with Export Options */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px'
            }}>
                <div>
                    <h3 style={{ margin: 0, color: '#1890ff' }}>
                        Asset Inventory Analytics - {assetType}
                    </h3>
                    <p style={{ margin: '4px 0 0 0', color: '#8c8c8c', fontSize: '12px' }}>
                        Generated on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <Tooltip title="Export analytics to PDF">
                        <Button 
                            size="small"
                            onClick={exportToPDF}
                            style={{
                                backgroundColor: '#ff4d4f',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            <img src={acrobat} alt="PDF" height={12} />
                            Export PDF
                        </Button>
                    </Tooltip>
                    
                    <Tooltip title="Export analytics to JPG image">
                        <Button 
                            size="small"
                            onClick={exportToJPG}
                            style={{
                                backgroundColor: '#52c41a',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                            }}
                        >
                            📷 Export JPG
                        </Button>
                    </Tooltip>
                </div>
            </div>

            {/* Overall Metrics */}
            <Row gutter={[16, 16]}>
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={`Total ${assetType}`}
                            value={analytics.totalAssets}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Total Value"
                            value={analytics.totalValue}
                            prefix="E"
                            precision={0}
                            valueStyle={{ color: '#722ed1' }}
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title={getFunctionalLabel()}
                            value={analytics.functionalRate}
                            suffix="%"
                            precision={1}
                            valueStyle={{ color: getPerformanceColor(analytics.functionalRate) }}
                        />
                        <Badge 
                            color={getPerformanceColor(analytics.functionalRate)} 
                            text={getPerformanceText(analytics.functionalRate)} 
                        />
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={6}>
                    <Card size="small">
                        <Statistic
                            title="Active Facilities"
                            value={analytics.distributionStats.facilitiesWithAssets}
                            suffix={`/ ${analytics.distributionStats.totalFacilities}`}
                            valueStyle={{ color: '#13c2c2' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Performance Indicators */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="Overall Performance">
                        <Progress
                            percent={Math.round(analytics.functionalRate)}
                            status={analytics.functionalRate >= 60 ? "success" : analytics.functionalRate >= 40 ? "normal" : "exception"}
                            strokeColor={getPerformanceColor(analytics.functionalRate)}
                        />
                        <div style={{ fontSize: '12px', color: '#8c8c8c', marginTop: '4px' }}>
                            System-wide {getFunctionalLabel().toLowerCase()}
                        </div>
                    </Card>
                </Col>
                
                <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="Top Performing Region">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#52c41a' }}>
                                {analytics.performanceMetrics.topPerformingRegionData ? 
                                    analytics.performanceMetrics.topPerformingRegionData.region : 
                                    'No data'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                {analytics.performanceMetrics.topPerformingRegionData ? 
                                    `${analytics.performanceMetrics.topPerformingRegionData.rate.toFixed(1)}% average ${getFunctionalLabel().toLowerCase()}` : 
                                    'Best regional average'}
                            </div>
                            {analytics.performanceMetrics.topPerformingRegionData && (
                                <div style={{ fontSize: '10px', color: '#595959', marginTop: '2px' }}>
                                    {analytics.performanceMetrics.topPerformingRegionData.facilityCount} facilities
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>

                <Col xs={24} sm={12} md={8}>
                    <Card size="small" title="Top Performing Level">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1890ff' }}>
                                {analytics.performanceMetrics.topPerformingLevelData ? 
                                    analytics.performanceMetrics.topPerformingLevelData.level : 
                                    'No data'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                {analytics.performanceMetrics.topPerformingLevelData ? 
                                    `${analytics.performanceMetrics.topPerformingLevelData.rate.toFixed(1)}% average ${getFunctionalLabel().toLowerCase()}` : 
                                    'Best level average'}
                            </div>
                            {analytics.performanceMetrics.topPerformingLevelData && (
                                <div style={{ fontSize: '10px', color: '#595959', marginTop: '2px' }}>
                                    {analytics.performanceMetrics.topPerformingLevelData.facilityCount} facilities
                                </div>
                            )}
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Facility Stock Levels Section */}
            <Divider>Facility Stock Levels & Performance</Divider>
            
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Card 
                        size="small" 
                        title={
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span>
                                    {showTopFacilities ? 'Top 20' : 'Bottom 20'} Performing Facilities by {getFunctionalLabel()}
                                </span>
                                <Button 
                                    type={showTopFacilities ? "primary" : "default"}
                                    size="small"
                                    onClick={() => setShowTopFacilities(!showTopFacilities)}
                                >
                                    {showTopFacilities ? 'Show Bottom 20' : 'Show Top 20'}
                                </Button>
                            </div>
                        }
                        style={{ minHeight: '500px' }}
                    >
                        <Table
                            columns={facilityStockColumns}
                            dataSource={getTopBottomFacilities()}
                            pagination={false}
                            size="small"
                            scroll={{ y: 400, x: 800 }}
                            rowKey="id"
                            rowClassName={(record, index) => {
                                if (showTopFacilities) {
                                    if (index < 5) return 'top-performer';
                                    if (index < 10) return 'good-performer';
                                    return 'average-performer';
                                } else {
                                    if (index < 5) return 'poor-performer';
                                    if (index < 10) return 'below-average-performer';
                                    return 'average-performer';
                                }
                            }}
                        />
                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#8c8c8c' }}>
                            <strong></strong> {getFunctionalLabel()} = 
                            ({asset_cat_id === '8' ? 'Good Condition' : asset_cat_id === '9' ? 'Usable' : 'Functional'})
                            <br />
                            <strong></strong>{asset_cat_id === '8' ? 'in good condition' : asset_cat_id === '9' ? 'usable' : 'functional'}, 
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Quick Performance Overview */}
            <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
                <Col xs={24}>
                    <Card size="small" title="Quick Performance Overview">
                        <Row gutter={[8, 8]}>
                            {analytics.facilityRankings.slice(0, 6).map((facility, index) => (
                                <Col key={facility.id} xs={24} sm={12} md={8} lg={4}>
                                    <div style={{ 
                                        padding: '12px', 
                                        border: '1px solid #f0f0f0', 
                                        borderRadius: '6px',
                                        backgroundColor: index < 3 ? '#f6ffed' : '#fafafa'
                                    }}>
                                        <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
                                            #{index + 1}
                                        </div>
                                        <Tooltip title={facility.facilityName || facility.business_name || facility.name}>
                                            <div style={{ 
                                                fontSize: '14px', 
                                                fontWeight: 'bold',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}>
                                                {((facility.facilityName || facility.business_name || facility.name || '').substring(0, 20))}
                                                {(facility.facilityName || facility.business_name || facility.name || '').length > 20 ? '...' : ''}
                                            </div>
                                        </Tooltip>
                                        <div style={{ 
                                            fontSize: '16px', 
                                            color: getPerformanceColor(facility.functionalRate),
                                            fontWeight: 'bold'
                                        }}>
                                            {facility.functionalRate.toFixed(1)}%
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                                            {facility.totalAssets} assets
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

const InventoryReport = () => {
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [showIntelligence, setShowIntelligence] = useState(false); // Add this line
    const [showWorkPlan, setShowWorkPlan] = useState(true);
    const isNational = util.isNational();
    
        const modules = util.getModules();
        const isSuper  = localStorage.getItem('is_admin')        === '1'
                  || localStorage.getItem('is_national')     === '1'
                  || localStorage.getItem('is_client_admin') === '1';
        const tabItems = [
            { k: 'Medical',     v: 'Med Assets',    cat_type: 'Med/NonMed', asset_cat_id: '3' },
            { k: 'Non Medical', v: 'Non Med Assets', cat_type: 'Med/NonMed', asset_cat_id: '2' },
            { k: 'Vehicle',     v: 'Vehicle',        cat_type: 'Med/NonMed', asset_cat_id: '8' },
            //{ k: 'Supplies',    v: 'Supplies',       cat_type: 'Med/NonMed', asset_cat_id: '9' },
        ];
    
        const filteredItems = isSuper ? tabItems : tabItems.filter(item => {
            if (item.k === 'Medical'     && modules.medical     === 1) return true;
            if (item.k === 'Non Medical' && modules.non_medical === 1) return true;
            if (item.k === 'Vehicle'     && modules.vehicle     === 1) return true;
            //if (item.k === 'Supplies'    && modules.supplies    === 1) return true;
            return false;
        });
      
    const [tab, setTab] = useState(filteredItems.length > 0 ? filteredItems[0].k : '');

    const onTabChange = (key) => {
        setTab(key);
    }

    useEffect(() => {
        FacilityService.all({ status: 1, include_me: 'Y' }).then(res => {
            let allClients = res.data.result.data.map(v => { return { id: v.id, name: v.business_name, is_national: v.is_national, is_me: v.is_me } });
            setClients(allClients);
        });

        AssetService.allItems({ status: 1 }).then(res => {
            let allItems = [];
            allItems = res.data.result.data.map((v) => {
                if (v.cat_type === 'Med/NonMed') {
                    v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                } else {
                    v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                }
                return v;
            });
            setItems(allItems);
        });

        FacilityService.allStationPoints({ status: 1 }).then(res => {
            setStationPoints([...res.data.result.data]);
        });
        
        AssetService.allFunctionalStatus().then(res => {
            setFunctionalStatus([...res.data.data]);
        })
    }, []);

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Assets Inventory Report</h2>
                </div>
                <div className="my-auto ml-auto">
    {isNational === 1 && (
        <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
    type={showWorkPlan ? "primary" : "default"}
    onClick={() => setShowWorkPlan(!showWorkPlan)}
    size="small"
    icon={<CalendarOutlined />}
>
    {showWorkPlan ? 'Hide Work Plan' : 'Show Work Plan'}
</Button>
            <Button 
                type={showIntelligence ? "primary" : "default"}
                onClick={() => setShowIntelligence(!showIntelligence)}
                size="small"
                style={{ marginRight: '8px' }}
            >
                🎯 {showIntelligence ? 'Hide Intelligence' : 'Show Intelligence'}
            </Button>
            <Button 
                type={showAnalytics ? "primary" : "default"}
                onClick={() => setShowAnalytics(!showAnalytics)}
                size="small"
            >
                {showAnalytics ? 'Hide Analytics' : 'Show Analytics'}
            </Button>
        </div>
    )}
</div>
            </div>

            <div className="page-pad">
                <Tabs
                    activeKey={tab}
                    destroyInactiveTabPane
                    onChange={onTabChange}
                    items={filteredItems.map(rw => ({
                        key: rw.k,
                        label: rw.v,
                        children: (
                            <List
                                items={items}
                                stationPoints={stationPoints}
                                functionalStatus={functionalStatus}
                                cat_type={rw.cat_type}
                                cat={rw.k}
                                asset_cat_id={rw.asset_cat_id}
                                clients={clients}
                                showAnalytics={showAnalytics}
                                 showIntelligence={showIntelligence}
                                 showWorkPlan={showWorkPlan}
                                assetType={rw.v}
                            />
                        )
                    }))}
                />
            </div>
        </div>
    )
}

function List(props) {
    const [result, setResult] = useState({ data: [], page: {} });
    const [result2, setResult2] = useState({ data: [], page: {} });
    const [result3, setResult3] = useState({ data: [], page: {} });
    const [analyticsData, setAnalyticsData] = useState([]);
    const [selectedLevel, setSelectedLevel] = useState(null);
    const [selectedRegion, setSelectedRegion] = useState(null);
    const sdataRef = useRef({ p: 1, ps: 25 });
    const [clients, setClients] = useState([]);
    const [items, setItems] = useState([]);
    const [stationPoints, setStationPoints] = useState([]);
    const [functionalStatus, setFunctionalStatus] = useState([]);
    const isNational = util.isNational();
    
    //const [showIntelligence, setShowIntelligence] = useState(true);

    const list = (p, ps, loadAnalytics = false) => {
        sdataRef.current.p = p || 1;
        sdataRef.current.ps = ps || sdataRef.current.ps;
        sdataRef.current.cat_type = props.cat_type;
        sdataRef.current.cat = props.cat;
        sdataRef.current.asset_cat_id = props.asset_cat_id;
        
        // For analytics, load all data with current filters applied
        const analyticsParams = loadAnalytics ? { ...sdataRef.current, ps: 0 } : sdataRef.current;
        
        util.showLoader();

        AssetInventoryService.lists1(analyticsParams).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            
            if (loadAnalytics) {
                setAnalyticsData(filteredData);
                // Update summary data for analytics as well
                loadSummaryData(true);
            } else {
                // Sort by totalitems in descending order (highest first)
                const sortedData = filteredData.sort((a, b) => (parseInt(b.totalitems) || 0) - (parseInt(a.totalitems) || 0));
                setResult({ ...data.result, data: sortedData });
            }
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })

        AssetInventoryService.lists10(sdataRef.current).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            setResult2({ ...data.result, data: filteredData });
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })

        AssetInventoryService.lists10sub(sdataRef.current).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            setResult3({ ...data.result, data: filteredData });
        }).catch(e => {
            message.error(e.message);
        }).finally(() => {
            util.hideLoader();
        })
    }

    // New function to load summary data with filters applied
    const loadSummaryData = (forAnalytics = false) => {
        const summaryParams = forAnalytics ? { ...sdataRef.current } : sdataRef.current;
        
        AssetInventoryService.lists10(summaryParams).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            setResult2({ ...data.result, data: filteredData });
        }).catch(e => {
            message.error(e.message);
        });

        AssetInventoryService.lists10sub(summaryParams).then(({ data }) => {
            const filteredData = data.result.data.filter(item => item.asset_cat_id === props.asset_cat_id);
            setResult3({ ...data.result, data: filteredData });
        }).catch(e => {
            message.error(e.message);
        });
    }

    // Load analytics data on component mount for national users
    const loadAnalyticsData = () => {
        if (isNational === 1) {
            list(1, 0, true);
        } else {
            list(sdataRef.current.p, sdataRef.current.ps);
        }
    };

    const filteredStatuses = useMemo(() => {
        return functionalStatus.filter(status => {
            // Exclude these statuses if asset_cat_id is '8'
            if (props.asset_cat_id === '8') {
                const excludedFor8 = [
                    'Not installed',
                    'Functional',
                    'Boarded',
                    'Non functional',
                    'Installed',
                    'Partial functional',
                    'Usable',
                    'Expired'
                ];
                return !excludedFor8.includes(status.name);
            }
            if (props.asset_cat_id === '9') {
                const excludedFor9 = [
                    'Good condition',
                    'Fair',
                    'Awaiting for disposal',
                    'Waiting for repairs',
                    'Not installed',
                    'Functional',
                    'Boarded',
                    'Non functional',
                    'Installed',
                    'Partial functional'
                ];
                return !excludedFor9.includes(status.name);
            }
            // Exclude these statuses if asset_cat_id is '3' or '2'
            if (props.asset_cat_id === '3' || props.asset_cat_id === '2') {
                const excludedFor3And2 = [
                    'Good condition',
                    'Fair',
                    'Awaiting for disposal',
                    'Waiting for repairs',
                    'Usable',
                    'Expired'
                ];
                return !excludedFor3And2.includes(status.name);
            }

            return true; // Include all other statuses for other asset_cat_id values
        });
    }, [functionalStatus, props.asset_cat_id]);

    const download = async () => {
        // Set asset_cat_id based on the current asset type from props
        sdataRef.current.asset_cat_id = props.asset_cat_id;
        sdataRef.current.all = 'ALL';
        
        util.showLoader(); 
        try {
            await ExcelRportUtil.generateStockTransactionsReport(sdataRef);
        } catch (error) {
            message.error('Failed to generate Excel report: ' + error.message);
        } finally {
            util.hideLoader();
        }
    }

    useEffect(() => {
        setClients(props.clients);
    }, [props.clients]);

    useEffect(() => {
        setItems(props.items.filter(v => v.asset_cat_id === props.asset_cat_id));
    }, [props.items, props.cat_type, props.asset_cat_id]);

    useEffect(() => {
        setStationPoints(props.stationPoints);
    }, [props.stationPoints]);

    useEffect(() => {
        setFunctionalStatus(props.functionalStatus);
    }, [props.functionalStatus]);

    useEffect(() => {
        loadAnalyticsData();
    }, [props.cat_type, props.cat, props.asset_cat_id]);

    const handleSearch = () => {
        list(sdataRef.current.p, sdataRef.current.ps);
        loadSummaryData(); // Update summary blocks with filters
        
        // Reload analytics data after search for national users with current filters
        if (isNational === 1) {
            setTimeout(() => {
                list(1, 0, true); // This will now include all current filters
            }, 500);
        }
    };

    const getTotalLabel = () => {
        switch (props.asset_cat_id) {
            case '8': return 'TOTAL VEHICLES';
            case '3': return 'TOTAL MEDICAL ASSETS';
            case '2': return 'TOTAL NON MEDICAL ASSETS';
            default: return 'TOTAL SUPPLIES';
        }
    };

    const getValueLabel = () => {
        switch (props.asset_cat_id) {
            case '8': return 'VEHICLE VALUE';
            case '3': return 'MEDICAL VALUE';
            case '2': return 'NON MEDICAL VALUE';
            default: return 'SUPPLIES VALUE';
        }
    };

    const getProcuredLabel = () => {
        switch (props.asset_cat_id) {
            case '8': return 'PROCURED VEHICLES';
            case '3': return 'PROCURED MEDICAL';
            case '2': return 'PROCURED NON MEDICAL';
            default: return 'PROCURED SUPPLIES';
        }
    };

    const getDonatedLabel = () => {
        switch (props.asset_cat_id) {
            case '8': return 'DONATED VEHICLES';
            case '3': return 'DONATED MEDICAL';
            case '2': return 'DONATED NON MEDICAL';
            default: return 'DONATED SUPPLIES';
        }
    };

    const getFunctionalLabel = () => {
        return props.asset_cat_id === '8' ? 'Good Condition' : 
               props.asset_cat_id === '9' ? 'Usable' : 'Functional';
    };

    const getTableHeaders = () => {
        if (props.asset_cat_id === '8') {
            return (
                <>
                    <th className="w100">Fair</th>
                    <th className="w100">Value</th>
                    <th className="w100">Good condition</th>
                    <th className="w100">Value</th>
                    <th className="w100">Waiting for repairs</th>
                    <th className="w100">Value</th>
                    <th className="w100">Awaiting for disposal</th>
                    <th className="w100">Value</th>
                </>
            );
        }
        
        if (props.asset_cat_id === '9') {
            return (
                <>
                    <th className="w100">Usable</th>
                    <th className="w100">Value</th>
                    <th className="w100">Expired</th>
                    <th className="w100">Value</th>
                </>
            );
        }
        else {
            return (
                <>
                    <th className="w100">Functional</th>
                    <th className="w100">Value</th>
                    <th className="w100">Partial functional</th>
                    <th className="w100">Value</th>
                    <th className="w100">Installed</th>
                    <th className="w100">Value</th>
                    <th className="w100">Not installed</th>
                    <th className="w100">Value</th>
                    <th className="w100">Non functional</th>
                    <th className="w100">Value</th>
                    <th className="w100">Boarded</th>
                    <th className="w100">Value</th>
                </>
            );
        }
    };

    const getTableData = (v) => {
        if (props.asset_cat_id === '8') {
            return (
                <>
                    <td style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.fair}</td>
                    <td style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.fairvalue}</td>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.good}</td>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.goodvalue}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.repairs}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.repairsvalue}</td>
                    <td style={{ marginTop: '4px', color: 'black', fontWeight: 700 }}>{v.disposal}</td>
                    <td style={{ marginTop: '4px', color: 'black', fontWeight: 700 }}>{v.disposalvalue}</td>
                </>
            );
        } 
        if (props.asset_cat_id === '9') {
            return (
                <>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.usable}</td>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.usablevalue}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.expired}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.expiredvalue}</td>
                </>
            );
        } 
        else {
            return (
                <>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.functional}</td>
                    <td style={{ marginTop: '4px', color: 'green', fontWeight: 700 }}>{v.functionalvalue}</td>
                    <td style={{ marginTop: '4px', color: 'lightgreen', fontWeight: 700 }}>{v.partialfunctional}</td>
                    <td style={{ marginTop: '4px', color: 'lightgreen', fontWeight: 700 }}>{v.partialfunctionalvalue}</td>
                    <td style={{ marginTop: '4px', color: 'gray', fontWeight: 700 }}>{v.installed}</td>
                    <td style={{ marginTop: '4px', color: 'gray', fontWeight: 700 }}>{v.installedvalue}</td>
                    <td style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.not_installed}</td>
                    <td style={{ marginTop: '4px', color: 'orange', fontWeight: 700 }}>{v.not_installedvalue}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.nonfunctional}</td>
                    <td style={{ marginTop: '4px', color: 'red', fontWeight: 700 }}>{v.nonfunctionalvalue}</td>
                    <td style={{ marginTop: '4px', color: 'black', fontWeight: 700 }}>{v.boarded}</td>
                    <td style={{ marginTop: '4px', color: 'black', fontWeight: 700 }}>{v.boardedvalue}</td>
                </>
            );
        }
    };

    const blockStyle = {
        backgroundColor: 'rgba(173, 216, 230, 0.5)',
        border: '1px solid #ccc',
        padding: '10px',
        flex: '1 1 100px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        margin: '5px',
    };
    const blockStyle2 = {
        backgroundColor: 'rgba(144, 238, 144, 0.5)',
        border: '1px solid #ccc',
        padding: '10px',
        flex: '1 1 100px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        margin: '5px',
    };
    const blockStyle3 = {
        backgroundColor: 'rgba(255, 255, 224, 0.5)',
        border: '1px solid #ccc',
        padding: '10px',
        flex: '1 1 100px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '16px',
        margin: '5px',
    };

    const containerStyle = {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
    };

    function calculateFunctionalPercent(totalItems, functionalItems) {
        if (totalItems === 0) {
            return 0;
        }
        return (functionalItems / totalItems) * 100;
    }

    return (
        <div className="">
            <div className="page-pad" style={{ backgroundColor: 'rgba(128, 0, 0, 0.1)' }}>
                {/* Analytics Section - Load by default for national users */}
                {props.showAnalytics && isNational === 1 && (
                    <InventoryAnalytics 
                        data={analyticsData} 
                        clients={clients}
                        isNational={isNational}
                        assetType={props.assetType}
                        asset_cat_id={props.asset_cat_id}
                        selectedLevel={selectedLevel}
                        selectedRegion={selectedRegion}
                    />
                )}
    {props.showIntelligence && isNational === 1 && (
    <InventoryDataIntelligence 
        data={analyticsData} 
        clients={clients}
        assetType={props.assetType}
        asset_cat_id={props.asset_cat_id}
       // isVisible={showIntelligence}
        isVisible={props.showIntelligence}
    />
)}
{props.showWorkPlan && isNational === 1 && (
    <Workplanintegration 
        facilities={clients}
        inventoryData={analyticsData}
        selectedRegion={selectedRegion}
        selectedLevel={selectedLevel}
        assetType={props.assetType}
        isVisible={props.showWorkPlan}
    />
)}

                <div style={{ textAlign: 'center', fontWeight: 'bold' }}></div>

                {result.data.length > 0 &&
                    <div className="text-secondary my-auto">
                        Showing {result.page.start + 1} - {result.page.start + result.page.total} of {result.page.total_records} records.
                    </div>
                }

                <div className="d-flex tbl-search-head">
                    <div className="my-auto flex-grow-1 pr10">
                        <SearchForm
                            dataRef={sdataRef}
                            onSearch={handleSearch}
                            clients={clients}
                            functionalStatus={filteredStatuses}
                            stationPoints={stationPoints}
                            cat_type={props.cat_type}
                            asset_cat_id={props.asset_cat_id}
                            isNational={isNational}
                            selectedLevel={selectedLevel}
                            selectedRegion={selectedRegion}
                            onLevelChange={setSelectedLevel}
                            onRegionChange={setSelectedRegion}
                        />
                    </div>

                    <div className="my-auto d-flex" style={{ display: 'flex', alignItems: 'center' }}>
                        &nbsp;&nbsp;
                        <button
                            type="button"
                            onClick={download}
                            style={{
                                backgroundColor: 'green',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                padding: '10px',
                                marginRight: '10px'
                            }}
                        >
                            <img src={excel} alt="download" height={15} /> Export Excel
                        </button>
                    </div>
                </div>

                <If cond={result.data.length}>
                    <div className="table-responsive">
                        <table className="table table-bordered table-sm font-xs table-striped table-hover m-0">
                            <thead className="thead-light text-uppercase table-text-vmid pad-y-md">
                                <tr>
                                    <th className="w250">Item</th>
                                    <th className="w50">Total</th>
                                    <th className="w100">Total Value</th>
                                    {getTableHeaders()}
                                </tr>
                            </thead>
                            <tbody className="table-text-top font-sm">
                                {result.data.map((v, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div className="uc bold600">{v.item}</div>
                                        </td>
                                        <td style={{ marginTop: '4px', color: 'blue', fontWeight: 700 }}>{v.totalitems}</td>
                                        <td style={{ marginTop: '4px', color: 'blue', fontWeight: 700 }}>E{v.totalvalue}</td>
                                        {getTableData(v)}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="d-flex tbl-foot-bx">
                        <AntdPaging
                            onChange={list}
                            total={result.page.total_records}
                            current={result.page.cur_page}
                            pageSize={sdataRef.current.ps}
                            showSizeChanger
                        />
                    </div>
                </If>
                <If cond={!result.data.length}>
                    <div className="no-rec">No record found</div>
                </If>
            </div>
        </div>
    )
}

const SearchForm = (props) => {
    let { dataRef, onSearch, clients, isNational, cat_type, asset_cat_id, selectedLevel, selectedRegion, onLevelChange, onRegionChange } = props;
    let [data, setData] = useState({ ...dataRef.current, station_point_ids: [], isdonated: null });
    let [stationPoints, setStationPoints] = useState([]);
    let [functionalStatus, setFunctionalStatus] = useState([]);
    const [items, setItems] = useState([]);

    const handleChange = (v, k) => {
        data[k] = v;
        if (k === 'client_id') {
            data.station_point_ids = [];
        }
        setData({ ...data });
    }

    useEffect(() => {
        dataRef.current = { ...data };
    }, [data, dataRef]);

    useEffect(() => {
        if (isNational === 1) {
            setData({ ...data, client_id: null });
        } else {
            let ob = clients.find(v => v.is_me);
            setData({ ...data, client_id: ob?.id });
        }
    }, [clients]);

    useEffect(() => {
        setStationPoints([...props.stationPoints]);
    }, [props.stationPoints]);

    useEffect(() => {
        setFunctionalStatus([...props.functionalStatus]);
    }, [props.functionalStatus]);

    useEffect(() => {
        setData({ ...data, p: dataRef.current.p, ps: dataRef.current.ps });
    }, [dataRef.current.p, dataRef.current.ps]);

    useEffect(() => {
        AssetService.allItems({ status: 1, cat_type, asset_cat_id, client_id: data.client_id, availableAssetsOnly: 'Y' }).then(res => {
            let allItems = [];
            allItems = res.data.result.data.map((v) => {
                if (v.asset_cat_id === '8') {
                    v.item_full_name = v.item_full_name + ' [' + v.subcat + ']';
                } else {
                    v.item_full_name = v.item_full_name + ' [' + v.cat + ']';
                }
                return v;
            });
            setItems(allItems);
        });
    }, [cat_type, data.client_id, asset_cat_id]);

    const getStatusLabel = () => {
        switch (asset_cat_id) {
            case '8': return 'Vehicle Status';
            case '3': return 'Functional Status';
            case '2': return 'Functional Status';
            default: return 'Status';
        }
    };

    return (
        <form onSubmit={e => e.preventDefault()} autoComplete="off" spellCheck="false">
            <div className="d-flex">
                {isNational === 1 && <div className="w210 mr5">
                    <div className="text-secondary fs11 mb2">Region</div>
                    <AntdSelect
                        options={[
                            { value: 'Manzini', label: "Manzini" },
                            { value: 'Hhohho', label: "Hhohho" },
                            { value: 'Shiselweni', label: "Shiselweni" },
                            { value: 'Lubombo', label: "Lubombo" }
                        ]}
                        allowClear
                        value={data.region}
                        onChange={v => { 
                            handleChange(v, 'region');
                            onRegionChange && onRegionChange(v);
                        }}
                    />
                </div>}
                {isNational === 1 && (
                    <div className="w210 mr5">
                        <div className="text-secondary fs11 mb2">
                            Level 
                            <Tooltip title="Select level to activate comparative analytics">
                                <span style={{ color: '#1890ff', marginLeft: '4px' }}>ⓘ</span>
                            </Tooltip>
                        </div>
                        <AntdSelect
                            options={[
                                { value: 'Level 1', label: "Level 1" },
                                { value: 'Level 2', label: "Level 2" },
                                { value: 'Level 3', label: "Level 3" },
                                { value: 'Level 4', label: "Level 4" },
                                { value: 'Level 5', label: "Level 5" }
                            ]}
                            allowClear
                            value={data.site_type}
                            onChange={v => { 
                                handleChange(v, 'site_type');
                                onLevelChange && onLevelChange(v);
                            }}
                        />
                    </div>
                )}

                {isNational === 1 &&
                    <div className="w300 mr5">
                        <div className="text-secondary fs11 mb2">Facility</div>
                        <AntdSelect
                            placeholder="All Facilities"
                            allowClear
                            showSearch
                            options={clients}
                            value={data.client_id}
                            onChange={v => {
                                data.item_id = null;
                                handleChange(v, 'client_id');
                            }}
                        />
                    </div>
                }

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">Location</div>
                    <AntdSelect
                        showSearch
                        allowClear
                        placeholder="Station Point"
                        mode="multiple"
                        options={stationPoints.filter(v => v.client_ids.includes(data.client_id))}
                        value={data.station_point_ids}
                        onChange={v => {
                            handleChange(v, 'station_point_ids');
                        }}
                    />
                </div>

                <div className="w350 mr5">
                    <div className="text-secondary fs11 mb2">Asset</div>
                    <AntdSelect
                        placeholder="Assets (All)"
                        allowClear
                        showSearch
                        options={items.map(v => { return { id: v.id, name: v.item_full_name } })}
                        value={data.item_id}
                        onChange={v => handleChange(v, 'item_id')}
                    />
                </div>

                <div className="w200 mr5">
                    <div className="text-secondary fs11 mb2">{getStatusLabel()}</div>
                    <AntdSelect
                        placeholder="Status"
                        showSearch
                        allowClear
                        options={functionalStatus}
                        value={data.functional_status}
                        onChange={v => handleChange(v, 'functional_status')}
                    />
                </div>

                <div className="pt15">
                    <Button className="mt4" type="primary" onClick={() => onSearch()}>
                        <img src={search} alt='search' className='h-1 mr5' height={15} />
                    </Button>
                </div>
            </div>
        </form>
    )
}

export default InventoryReport